import { useStateStore } from "@/context/stateStore";
import { IPlant } from "@/models/types/plant";
import { ICycle } from "@/models/types/cycle";
import { INote } from "@/models/types/note";
import { ICatalogPlant } from "@/models/types/catalogPlant";

let activeInit: Promise<void> | null = null;

export async function InitializeData({ urlToUse, userEmail, force = false }: { urlToUse: string; userEmail: string; force?: boolean }) {

    const normalizedEmail = userEmail.trim().toLowerCase();
    const state = useStateStore.getState();

    if (!urlToUse || urlToUse.trim() === '' || !normalizedEmail) {
        state.clearAppData();
        return;
    }

    if (!force && state.dataHydrated && state.dataOwnerEmail === normalizedEmail) {
        return;
    }

    if (activeInit) {
        return activeInit;
    }

    state.setDataLoading(true);
    state.setDataError(null);

    activeInit = (async () => {
        const res = Promise.all([
            fetch(`${urlToUse}/api/plants/get`, { credentials: 'include' }),
            fetch(`${urlToUse}/api/cycle/get`, { credentials: 'include' }),
            fetch(`${urlToUse}/api/note/get`, { credentials: 'include' }),
            fetch(`${urlToUse}/api/catalog`, { credentials: 'include' }),
        ])
        if (!res) {
            throw new Error("Failed to fetch initial data");
        }
        const [plantsResponse, cyclesResponse, notesResponse, catalogResponse] = await res;

        if (!plantsResponse || !cyclesResponse || !notesResponse || !catalogResponse) {
            throw new Error("Failed to fetch initial data");
        }
        if (!plantsResponse.ok || !cyclesResponse.ok || !notesResponse.ok || !catalogResponse.ok) {
            throw new Error("Failed to fetch initial data");
        }
        const plantsData = await plantsResponse.json();
        const cyclesData = await cyclesResponse.json();
        const notesData = await notesResponse.json();
        const catalogData = await catalogResponse.json();
        if (!plantsData || !cyclesData || !notesData || !catalogData) {
            throw new Error("Invalid data format received");
        }
        if (plantsData.status !== 200 || cyclesData.status !== 200 || notesData.status !== 200 || catalogData.status !== 200) {
            throw new Error("Failed to initialize data");
        }
        const plants = plantsData.data as IPlant[];
        const cycles = cyclesData.data as ICycle[];
        const notes = notesData.data as INote[];
        const catalogPlants = catalogData.data as ICatalogPlant[];
        if (!plants || !cycles || !notes || !catalogPlants) {
            throw new Error("Invalid data format received");
        }

        useStateStore.getState().setPlants([...plants]);
        useStateStore.getState().setActivelyGrowing([...cycles]);
        useStateStore.getState().setNotes([...notes]);
        useStateStore.getState().setCatalogPlants([...catalogPlants]);
        useStateStore.getState().setDataOwnerEmail(normalizedEmail);
        useStateStore.getState().setDataHydrated(true);
    })();

    try {
        await activeInit;
    } catch (error) {

        console.error("Error initializing data:", error);
        useStateStore.getState().setDataError("Failed to load your garden data.");
        useStateStore.getState().setDataHydrated(false);

    } finally {
        useStateStore.getState().setDataLoading(false);
        activeInit = null;
    }
}
